/**
 * Exposure choices of the Player's ☀ panel, saved per device like the touch settings
 * (issue walk-bake#11, C8). They reach the viewer as URL parameters when a walk opens and
 * through `window.walkbake.set` while it is open. The "test options" are temporary: Santi picks
 * the defaults on devices, then they stop being options.
 */

import { clampRound, type NumericRange } from "./numeric.ts";

export type AutoExposureSpeed = "slow" | "medium" | "fast";
export type AutoExposureMeter = "centre" | "average" | "highlights";

export interface ExposureSettings {
  /** Automatic exposure on/off. */
  auto: boolean;
  /** Manual exposure in stops, added on top of the automatic value. */
  exposure: number;
  /** Share of the brightness difference the automatic exposure compensates. */
  strength: number;
  /** Largest automatic correction, in stops either way. */
  range: number;
  speed: AutoExposureSpeed;
  meter: AutoExposureMeter;
}

export const EXPOSURE_RANGE: NumericRange = { min: -2, max: 2, step: 0.01 };
/** Auto exposure strength, 0.1..1 (the old options were 0.6, 0.8, 1). */
export const STRENGTH_RANGE: NumericRange = { min: 0.1, max: 1, step: 0.01 };
/** Auto exposure largest correction in stops, 1..3 (the old options were 2 and 3). */
export const AE_RANGE: NumericRange = { min: 1, max: 3, step: 0.01 };
export const SPEEDS: AutoExposureSpeed[] = ["slow", "medium", "fast"];
export const METERS: AutoExposureMeter[] = ["centre", "average", "highlights"];

/** Today's behaviour (C3). */
export const DEFAULT_EXPOSURE_SETTINGS: ExposureSettings = {
  auto: true,
  exposure: 0,
  strength: 0.6,
  range: 2,
  speed: "slow",
  meter: "centre",
};

const STORAGE_KEY = "walk-player.exposure";

/** Keeps only valid values; anything else falls back to the default. */
function sanitize(raw: Partial<Record<keyof ExposureSettings, unknown>>): ExposureSettings {
  const d = DEFAULT_EXPOSURE_SETTINGS;
  const num = (value: unknown, range: NumericRange, fallback: number): number =>
    typeof value === "number" && Number.isFinite(value) ? clampRound(range, value) : fallback;
  return {
    auto: typeof raw.auto === "boolean" ? raw.auto : d.auto,
    exposure: num(raw.exposure, EXPOSURE_RANGE, d.exposure),
    strength: num(raw.strength, STRENGTH_RANGE, d.strength),
    range: num(raw.range, AE_RANGE, d.range),
    speed: SPEEDS.includes(raw.speed as AutoExposureSpeed) ? (raw.speed as AutoExposureSpeed) : d.speed,
    meter: METERS.includes(raw.meter as AutoExposureMeter) ? (raw.meter as AutoExposureMeter) : d.meter,
  };
}

export function loadExposureSettings(): ExposureSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return sanitize(JSON.parse(raw) as Partial<Record<keyof ExposureSettings, unknown>>);
  } catch {
    // Blocked or corrupt storage: fall back to defaults.
  }
  return { ...DEFAULT_EXPOSURE_SETTINGS };
}

export function saveExposureSettings(settings: ExposureSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Not persisted; still applies for this session.
  }
}

/**
 * Overrides from the Player's own URL (`?ae=0&ae_strength=1&ae_range=3&ae_speed=fast&ae_meter=highlights`),
 * for tests. They win over the saved settings for the walk opened in that page, and are not saved.
 */
export function withUrlOverrides(settings: ExposureSettings, search: string): ExposureSettings {
  const params = new URLSearchParams(search);
  const number = (key: string): number | undefined => {
    const value = params.get(key);
    return value === null || value === "" ? undefined : Number(value);
  };
  const ae = params.get("ae");
  return sanitize({
    ...settings,
    ...(ae !== null && { auto: ae !== "0" }),
    ...(number("ae_strength") !== undefined && { strength: number("ae_strength") }),
    ...(number("ae_range") !== undefined && { range: number("ae_range") }),
    ...(params.has("ae_speed") && { speed: params.get("ae_speed") }),
    ...(params.has("ae_meter") && { meter: params.get("ae_meter") }),
  });
}

/** Query string for the viewer page, so the walk opens with these settings (the viewer parses it). */
export function viewerQuery(settings: ExposureSettings, extra: { gpuMb?: number; slide?: number; fps?: boolean; kiosk?: boolean } = {}): string {
  const params = new URLSearchParams({
    ae: settings.auto ? "1" : "0",
    ae_strength: String(settings.strength),
    ae_range: String(settings.range),
    ae_speed: settings.speed,
    ae_meter: settings.meter,
  });
  if (settings.exposure !== 0) params.set("exposure", String(settings.exposure));
  if (extra.gpuMb !== undefined) params.set("gpu_mb", String(extra.gpuMb)); // the viewer shows it, see public/viewer
  if (extra.slide !== undefined) params.set("slide", String(extra.slide)); // how far a blocked step may turn to glide
  if (extra.fps) params.set("fps", "1"); // floating FPS box inside the viewer
  if (extra.kiosk) params.set("kiosk", "1"); // the viewer's bottom bar starts folded into its dot
  return params.toString();
}

/** Viewer switches the Player's own URL passes through unchanged, for A/B tests on a device
 *: `lm_format=auto|astc|rgb9e5|half`, `tex_format=orig`, `msaa=0|1`, `release=0`, `debug=1`. */
const PASS_THROUGH = ["lm_format", "tex_format", "msaa", "release", "debug"];

export function passThroughQuery(search: string): string {
  const from = new URLSearchParams(search);
  const params = new URLSearchParams();
  for (const key of PASS_THROUGH) {
    const value = from.get(key);
    if (value !== null) params.set(key, value);
  }
  return params.toString();
}

/** The viewer's `window.walkbake`, as far as the panel uses it. */
export interface WalkbakeApi {
  set(values: Record<string, unknown>): void;
  readonly exposure: number;
  readonly aeValue: number | null;
}

export function walkbakeOf(win: Window | null | undefined): WalkbakeApi | null {
  const api = (win as unknown as { walkbake?: Partial<WalkbakeApi> } | null | undefined)?.walkbake;
  return typeof api?.set === "function" ? (api as WalkbakeApi) : null;
}

/** Pushes changed settings into an open viewer. */
export function applyToViewer(api: WalkbakeApi | null, patch: Partial<ExposureSettings>, extra: Record<string, unknown> = {}): void {
  if (!api) return;
  api.set({
    ...extra,
    ...(patch.auto !== undefined && { ae: patch.auto }),
    ...(patch.exposure !== undefined && { exposure: patch.exposure }),
    ...(patch.strength !== undefined && { aeStrength: patch.strength }),
    ...(patch.range !== undefined && { aeRange: patch.range }),
    ...(patch.speed !== undefined && { aeSpeed: patch.speed }),
    ...(patch.meter !== undefined && { aeMeter: patch.meter }),
  });
}
